import LabeledBox from "../../components/LabeledBox";
import { dateFormat } from "../../helpers/dateTimeHelpers";
import { useQueryClient } from "@tanstack/react-query";
import Invoice from "../Invoice";
import { getOrderStatusInfo } from "@raashidh/shared";
import EmptyCollapsable from "../../components/EmptyCollapsable";
import OrderCardOrderItemsList from "./OrderItemsList";
import MyRow from "../../components/MyRow";
import MyDialogButton from "../../MY_COMPONENTS/MyDialogButton";
import PayDialogButton from "../../PAYMENT/PayDialogButton";

const OrderCardUser = ({ order }: any) => {
  if (!order?.order_id) return null;
  const queryClient = useQueryClient();

  /* ---------------- ITEMS ---------------- */
  const items = order.items ?? [];

  /* ---------------- AMOUNTS ---------------- */
   const totalItems = order.order_total_items ?? 0;
  const subtotal = Number(order.order_total_amount) / 100 ?? 0;
  const tax = order.order_tax ?? 0;
  const finalAmount = order?.order_final_amount_display ?? 0;

  /* ---------------- PAYMENT ---------------- */
  const isPaid = order.order_payment_confirmed === 1;

  /* ---------------- STATUS ---------------- */
  const status = order.order_status ?? "PENDING_PAYMENT";
  const statusInfo = getOrderStatusInfo(status as any);
  const statusColor = statusInfo?.color ?? "bg-gray-100";
  const statusIcon = statusInfo?.icon ?? "";

  /* ---------------- CUSTOMER ---------------- */
  const customerName = order.order_customer_name ?? "Unknown";
  const customerEmail = order.order_customer_email ?? "-";
  const customerPhone = order.order_customer_phone ?? "-";
  const deliveryLocation = order.order_delivery_location ?? "-";

  /* ---------------- TITLE ---------------- */
  const title = (
    <div className="w-full flex justify-between items-center text-xs text-gray-700">
      <span className="bg-gray-50 rounded px-2 line-clamp-1">
        #{order.order_id} — {customerName}
      </span>
      <span
        className={`h-4 font-bold px-1 rounded ${statusColor} flex items-center gap-1`}
      >
        <span className="text-sm">{statusIcon}</span>
        {status}
      </span>
    </div>
  );

  const refresh = async () => {
    await queryClient.invalidateQueries({ queryKey: ["userOrders"] });
  };

  return (
    <EmptyCollapsable
      title={title}
      collapsed={false}
      styles="w-fit bg-white border rounded-lg"
    >
      <div className="w-full p-2 flex flex-col gap-2 text-sm text-gray-700">

        {/* ITEMS */}
        <LabeledBox label="Ordered Items">
          <OrderCardOrderItemsList items={items} />
        </LabeledBox>

        {/* SUMMARY */}
        <LabeledBox label="Order Summary">
          <MyRow label="Total Items" value={totalItems} />
          <MyRow label="Subtotal" value={subtotal} />
          <MyRow label="Tax" value={tax} />
          <MyRow
            label="Final Amount"
            value={finalAmount}
            valueStyle="font-bold text-green-700"
          />
        </LabeledBox>

        {/* STATUS */}
        <LabeledBox label="Status">
          <MyRow label="Order Status" value={status} />
          <MyRow
            label="Payment"
            value={isPaid ? "Confirmed" : "Pending"}
            valueStyle={isPaid ? "text-green-700" : "text-red-600"}
          />

          {!isPaid && (
            <PayDialogButton
              order_id={order?.order_id}
              itemId={order.order_id}
              amount={order?.order_final_amount}
              refresh={refresh}
            />
          )}

          <div className="flex flex-col gap-0 mt-1">
            <RowSlim value={`Created: ${dateFormat(order.created_at)}`} />
            <RowSlim value={`Updated: ${dateFormat(order.updated_at)}`} />
            {isPaid && order.order_payment_confirmed_at && (
              <RowSlim
                value={`Payment Confirmed: ${dateFormat(
                  order.order_payment_confirmed_at
                )}`}
              />
            )}
          </div>
        </LabeledBox>

        {/* CUSTOMER INFO */}
        <LabeledBox label="Customer Info">
          <MyRow label="Name" value={customerName} />
          <MyRow label="Email" value={customerEmail} />
          <MyRow label="Phone" value={customerPhone || "-"} />
          <MyRow label="Address" value={deliveryLocation} />
        </LabeledBox>

        {/* DELIVERY */}
        <LabeledBox label="Delivery Details">
          <MyRow label="Method" value={order.order_delivery_method ?? "-"} />
          <MyRow label="Fee" value={order.order_delivery_fee ?? 0} />
        </LabeledBox>

        {/* NOTES */}
        {order.order_notes && (
          <LabeledBox label="Notes">
            <div>{order.order_notes}</div>
          </LabeledBox>
        )}

        {/* ACTIONS */}
        <div className="flex justify-end text-blue-600 text-xs underline gap-4 mt-1">
          <MyDialogButton
            textButton
            label="Invoice"
            dialogContent={<Invoice order={order} />}
          />
        </div>
      </div>
    </EmptyCollapsable>
  );
};

export default OrderCardUser;

/* ---------------- SMALL ROW ---------------- */
type RowSlimProps = {
  value: string | number | null | undefined;
};

const RowSlim: React.FC<RowSlimProps> = ({ value }) => {
  if (value === null || value === undefined) return null;
  return <div className="text-[10px] text-green-600">{value}</div>;
};